import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import { Store } from '@ngrx/store';
import { DialogCloseOptions, DialogCloseReason } from '@sui/dialog';
import { filter, Observable, take, tap } from 'rxjs';
import {
  loadUserCategories,
  toggleUserCategorySelection,
} from 'src/app/core/actions/post.actions';
import { NomMassUpdateOperation } from 'src/app/core/constants';
import {
  selectCompanyCategories,
  selectGroupedSelectedCategories,
} from 'src/app/core/reducers';
import { AppState } from 'src/app/reducers';
import {
  TabDialogBase,
  TabDialogDataBase,
} from 'src/app/shared/abstract/tab-dialog-base';
import { SearchableDialog, TabConfig } from 'src/app/shared/utils';
import { PostUserCategoriesListComponent } from '../post-user-categories/post-user-categories-list/post-user-categories-list.component';
import { CompanyCategory } from 'src/app/core/models/core.models';
import { faList, faTags } from '@fortawesome/free-solid-svg-icons';

interface DialogData extends TabDialogDataBase {
  postIds: string[];
  selectedPostsFeedId: string;
}

interface DialogResult {}

@Component({
  selector: 'app-post-user-categories-dialog',
  standalone: false,
  templateUrl: './post-user-categories-dialog.component.html',
  styleUrl: './post-user-categories-dialog.component.scss',
})
export class PostUserCategoriesDialogComponent
  extends TabDialogBase<DialogData, DialogResult>
  implements SearchableDialog, OnInit, AfterViewInit
{
  protected destroyRef = inject(DestroyRef);
  private store = inject(Store<AppState>);

  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;

  objectActionIcon: IconDefinition | IconProp;
  objectIcon: IconDefinition | IconProp;

  objectActionTitle = '';
  objectTitle = '';
  objectTitleDefault = this.translateService.instant(
    'popup.object-title.untitled',
  );

  // * Search Manager
  private tabSearch = this.enableTabSearch();
  onSearchInput = this.tabSearch.onSearchInput;

  hasActiveCategories: boolean = false;

  userCategories$: Observable<Array<any>>;

  NomMassUpdateOperation = NomMassUpdateOperation;
  updateOperationId: number = NomMassUpdateOperation.ADD;

  get searchString$() {
    return this.tabSearch.searchString$;
  }

  get visibleTabs(): TabConfig[] {
    return this.tabSearch.getVisibleTabs(this.tabs);
  }

  get postIds() {
    return this.dialogData.postIds;
  }

  get selectedPostsFeedId() {
    return this.dialogData.selectedPostsFeedId;
  }

  ngOnInit(): void {
    this.setupUIState();
    this.userCategories$ = this.store.select(selectCompanyCategories);

    this.store.dispatch(
      loadUserCategories({
        postIds: this.postIds,
        selectedPostsFeedId: this.selectedPostsFeedId,
      }),
    );

    this.userCategories$
      .pipe(
        filter((userCategories): userCategories is CompanyCategory[] =>
          Array.isArray(userCategories),
        ),
        tap((userCategories) => {
          this.hasActiveCategories = userCategories.some((userCategory) =>
            userCategory.categories.some((category) => category.is_selected),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.setupTabs();
  }

  private setupUIState(): void {
    // apply translations and icons

    this.objectActionIcon = faTags;
    this.objectIcon = faList;

    this.objectActionTitle = this.translateService.instant(
      'post-user-categories.title.user-content-categories-list',
    );
    this.objectTitle = this.translateService.instant(
      'post-user-categories.title.user-content-categories',
    );
  }

  private setupTabs(): void {
    this.userCategories$
      .pipe(
        filter((userCategories): userCategories is CompanyCategory[] =>
          Array.isArray(userCategories),
        ),
        take(1),
        tap((userCategories) => {
          this.tabs = userCategories.map(
            (companyCategory: CompanyCategory) => ({
              id: companyCategory.company_category_name,
              label: companyCategory.company_category_name,
              icon: ['fas', 'tag'],
              component: PostUserCategoriesListComponent,
              inputs: {
                companyCategoryId: companyCategory.company_category_id,
              },
              count: companyCategory.count_selected,
            }),
          );

          // Assuming you want to select the first tab only once as well
          if (this.tabs.length > 0) {
            this.initializeBase();
            this.tabSearch.setupAutoSwitch(this.tabs);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  clearSelected() {
    this.userCategories$
      .pipe(
        filter((userCategories): userCategories is CompanyCategory[] =>
          Array.isArray(userCategories),
        ),
        take(1),
        tap((userCategories) => {
          userCategories.forEach((userCategory) => {
            userCategory.categories.forEach((category) => {
              if (category.is_selected) {
                this.store.dispatch(
                  toggleUserCategorySelection({
                    companyCategoryId: category.company_category_id,
                    categoryId: category.id,
                    selected: false,
                  }),
                );
              }
            });
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected override performSave(): DialogCloseOptions<DialogResult> {
    let selectedCategories;
    this.store
      .select(selectGroupedSelectedCategories)
      .pipe(
        take(1),
        tap((groupedSelectedCategories) => {
          selectedCategories = groupedSelectedCategories;
        }),
      )
      .subscribe();

    return {
      reason: DialogCloseReason.Save,
      data: {
        categories: selectedCategories,
        updateOperationId: this.updateOperationId,
      },
    };
  }

  save(): void {
    if (!this.validateAndShow()) {
      return;
    }

    const result = this.performSave();
    this.dirtyHandler.closeImmediately(result);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
